import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../../shared/components/ui/card';
import { Button } from '../../../shared/components/ui/button';
import { Input } from '../../../shared/components/ui/input';
import { Badge } from '../../../shared/components/ui/badge';
import { useNetwork, useRemoveFromNetwork } from '../hooks/useNetwork';
import { Search, Users, Loader2, Briefcase, UserCircle } from 'lucide-react';
import { NetworkConnection } from '../api/networkApi';
import { NetworkConnectionCard } from './NetworkConnectionCard';
import { CuratedProvidersPanel } from '@/shared/components/curated';

type RoleFilter = 'all' | 'SERVICE_PROVIDER' | 'FINANCIAL_ADVISOR';

export const MyNetwork = () => {
  const navigate = useNavigate();
  const { data: connections, isLoading, isError, error } = useNetwork();
  const removeFromNetworkMutation = useRemoveFromNetwork();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<RoleFilter>('all');

  // Extract unique categories from connections (SP only)
  const categories = useMemo(() => {
    if (!connections) return [];
    const uniqueCategories = new Set<string>();
    connections.forEach((conn) => {
      conn.target.serviceProvider?.categories.forEach(
        (cat: { category: { id: string; name: string } }) => {
          uniqueCategories.add(cat.category.name);
        },
      );
    });
    return Array.from(uniqueCategories);
  }, [connections]);

  // Filter connections based on search, category, and role
  const filteredConnections = useMemo(() => {
    if (!connections) return [];

    return connections.filter((conn) => {
      if (selectedRole !== 'all' && conn.target.role !== selectedRole) return false;

      const searchLower = searchQuery.toLowerCase();
      const companyName =
        conn.target.serviceProvider?.companyName ||
        conn.target.financialAdvisor?.companyName ||
        '';
      const contactName = [conn.target.firstName, conn.target.lastName]
        .filter(Boolean)
        .join(' ');
      const matchesSearch =
        searchQuery === '' ||
        companyName.toLowerCase().includes(searchLower) ||
        contactName.toLowerCase().includes(searchLower);

      const matchesCategory =
        selectedCategory === 'all' ||
        conn.target.serviceProvider?.categories.some(
          (cat: { category: { id: string; name: string } }) =>
            cat.category.name === selectedCategory,
        );

      return matchesSearch && matchesCategory;
    });
  }, [connections, searchQuery, selectedCategory, selectedRole]);

  const handleRemove = async (targetUserId: string, displayName: string) => {
    if (confirm(`Are you sure you want to remove ${displayName} from your network?`)) {
      try {
        await removeFromNetworkMutation.mutateAsync(targetUserId);
      } catch (err) {
        alert(err instanceof Error ? err.message : 'Failed to remove professional.');
      }
    }
  };

  const handleViewProfile = (connection: NetworkConnection) => {
    if (connection.target.role === 'SERVICE_PROVIDER' && connection.target.serviceProvider) {
      navigate(`/providers/${connection.target.serviceProvider.id}`);
    } else if (connection.target.role === 'FINANCIAL_ADVISOR') {
      navigate(`/advisors/${connection.target.id}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#026857] animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading your network...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="p-8 text-center max-w-md">
          <div className="text-red-600 mb-4">
            <Users className="w-16 h-16 mx-auto mb-2" />
            <h2 className="text-xl font-semibold">Error Loading Network</h2>
          </div>
          <p className="text-gray-600 mb-4">
            {error instanceof Error ? error.message : 'Failed to load your network connections.'}
          </p>
          <Button
            onClick={() => window.location.reload()}
            className="bg-[#026857] hover:bg-[#025247]"
          >
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl text-gray-900 mb-2">My Professional Network</h1>
          <p className="text-gray-600">Manage your trusted network of professionals</p>
        </div>

        {/* Stats and Search Bar */}
        <div className="mb-6 flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <Badge className="bg-[#026857] hover:bg-[#026857] text-lg px-4 py-2">
            {filteredConnections.length}{' '}
            {filteredConnections.length === 1 ? 'Connection' : 'Connections'}
          </Badge>
          <div className="flex-1 max-w-md">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search by name or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </div>

        {/* Role Filter */}
        <div className="mb-4 flex flex-wrap gap-2">
          <Button
            variant={selectedRole === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedRole('all')}
            className={selectedRole === 'all' ? 'bg-[#026857] hover:bg-[#025247]' : ''}
          >
            All Professionals
          </Button>
          <Button
            variant={selectedRole === 'SERVICE_PROVIDER' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedRole('SERVICE_PROVIDER')}
            className={selectedRole === 'SERVICE_PROVIDER' ? 'bg-[#026857] hover:bg-[#025247]' : ''}
          >
            <Briefcase className="w-4 h-4 mr-1" />
            Service Providers
          </Button>
          <Button
            variant={selectedRole === 'FINANCIAL_ADVISOR' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedRole('FINANCIAL_ADVISOR')}
            className={selectedRole === 'FINANCIAL_ADVISOR' ? 'bg-[#026857] hover:bg-[#025247]' : ''}
          >
            <UserCircle className="w-4 h-4 mr-1" />
            Financial Advisors
          </Button>
        </div>

        {/* Category Filter (only show when SP filter is active or all) */}
        {categories.length > 0 && selectedRole !== 'FINANCIAL_ADVISOR' && (
          <div className="mb-6 flex flex-wrap gap-2">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory('all')}
              className={selectedCategory === 'all' ? 'bg-[#026857] hover:bg-[#025247]' : ''}
            >
              All Categories
            </Button>
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category ? 'bg-[#026857] hover:bg-[#025247]' : ''}
              >
                {category}
              </Button>
            ))}
          </div>
        )}

        {/* Empty State */}
        {connections && connections.length === 0 && (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl text-gray-900 mb-2">No Connections Yet</h3>
            <p className="text-gray-600 mb-6">
              Start building your professional network by adding trusted professionals.
            </p>
            <Button className="bg-[#026857] hover:bg-[#025247]">Browse Professionals</Button>
          </Card>
        )}

        {/* Filtered Empty State */}
        {connections && connections.length > 0 && filteredConnections.length === 0 && (
          <Card className="p-12 text-center">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl text-gray-900 mb-2">No Results Found</h3>
            <p className="text-gray-600 mb-6">
              No connections match your search criteria. Try adjusting your filters.
            </p>
            <Button
              variant="outline"
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('all');
                setSelectedRole('all');
              }}
            >
              Clear Filters
            </Button>
          </Card>
        )}

        {/* Connections List */}
        {filteredConnections.length > 0 && (
          <div className="grid gap-6">
            {filteredConnections.map((connection) => (
              <NetworkConnectionCard
                key={connection.id}
                connection={connection}
                onViewProfile={handleViewProfile}
                onRemove={handleRemove}
                isRemoving={removeFromNetworkMutation.isPending}
              />
            ))}
          </div>
        )}

        {/* Feature 096 — global curated content, deliberately outside the
            search/filter state above. Identical for SPs and FAs. */}
        <CuratedProvidersPanel
          variant="grid"
          divider
          onMessage={(userId) => navigate(`/messages?recipient=${userId}`)}
          onViewProfile={(serviceProviderId) => navigate(`/providers/${serviceProviderId}`)}
        />
      </div>
    </div>
  );
};
